import { useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

// ──────────────────────────────────────────────────────────
// CIDR validation
// ──────────────────────────────────────────────────────────

const CIDR_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(\/(\d{1,2}))?$/;

export function isValidCidr(v: string): boolean {
  const m = CIDR_RE.exec(v.trim());
  if (!m) return false;
  for (let i = 1; i <= 4; i++) {
    if (Number(m[i]) > 255) return false;
  }
  if (m[6] !== undefined && Number(m[6]) > 32) return false;
  return true;
}

// ──────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────

export interface CidrTagInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  label?: string;
  disabled?: boolean;
  /** aria-describedby id to point at helper text */
  describedBy?: string;
}

export function CidrTagInput({ value, onChange, disabled, describedBy }: CidrTagInputProps) {
  const [inputVal, setInputVal] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function tryAdd(raw: string) {
    const v = raw.trim().replace(/,+$/, '').trim();
    if (!v) return;

    if (!isValidCidr(v)) {
      setError(`"${v}" is not a valid CIDR (e.g. 192.0.2.0/24)`);
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }

    if (value.includes(v)) {
      setInputVal('');
      return;
    }

    onChange([...value, v]);
    setInputVal('');
    setError('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      tryAdd(inputVal);
    } else if (e.key === 'Backspace' && !inputVal && value.length > 0) {
      onChange(value.slice(0, -1));
      setError('');
    }
  }

  function handleBlur() {
    if (inputVal.trim()) {
      tryAdd(inputVal);
    }
  }

  function removeAt(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  return (
    <div>
      {/* Tag container + input */}
      <div
        className={cn(
          'min-h-9 flex flex-wrap gap-1.5 items-center rounded-md border border-input bg-background px-3 py-2',
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-60',
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((cidr, i) => (
          <span
            key={cidr}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-xs font-mono text-slate-700"
          >
            {cidr}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${cidr}`}
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                className="inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors h-3.5 w-3.5"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputVal}
          onChange={(e) => { setInputVal(e.target.value); setError(''); }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={value.length === 0 ? '192.0.2.0/24' : ''}
          className={cn(
            'flex-1 min-w-24 bg-transparent text-sm font-mono outline-none placeholder:text-muted-foreground/50',
            disabled && 'cursor-not-allowed',
            shake && 'animate-[shake_0.3s_ease-in-out]',
          )}
          aria-describedby={describedBy}
          aria-invalid={!!error}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {/* Inline validation error */}
      {error && (
        <p role="alert" className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
